import { useAuth } from '@clerk/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { ApiError } from '@/api/client';
import { getPortalChecklist } from '@/api/onboardingChecklist';
import { listPortalFiles } from '@/api/portalFiles';
import {
  savePortalIntakeDraft,
  submitPortalIntake,
  type IntakeAnswers,
  type IntakeAnswerValue,
  type IntakeOut,
} from '@/api/portalIntake';
import { CustomerUploadSection } from '@/components/CustomerUploadSection';
import { IntakeInput } from '@/components/IntakeInput';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ProgressBar } from '@/components/ui/progress';
import { canAdvance, requiredComplete, resumeIndex, wizardProgress } from '@/lib/onboardingWizard';
import { isAnswered } from '@/lib/portalIntake';
import { groupByCategory, UPLOAD_CATEGORIES } from '@/lib/portalOnboarding';

/** First-sign-in onboarding walkthrough: one question per card with a
 *  progress bar, so the customer answers a short focused prompt instead
 *  of facing the whole questionnaire at once. Answers save on every
 *  advance (the API merges partial payloads), so refreshing mid-way
 *  resumes at the first unanswered question. Optional questions can be
 *  skipped and filled in later on the dashboard; required ones gate
 *  Next. Finishing submits the questionnaire and continues into the
 *  document steps — one upload page per category with guidance and an
 *  example template — closing on a checklist summary of what we have
 *  and what's still missing.
 */
export function OnboardingWizard({
  engagementId,
  intake,
  onExit,
}: {
  engagementId: string;
  intake: IntakeOut;
  onExit: () => void;
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const questions = intake.intake_schema.questions;

  const [local, setLocal] = useState<IntakeAnswers>({});
  const answers: IntakeAnswers = { ...intake.answers, ...local };
  const [index, setIndex] = useState(() => resumeIndex(questions, intake.answers));
  // "Resumed" means any persisted answer exists — not resumeIndex > 0,
  // which is also 0 when every question is answered (single-question
  // schemas would otherwise re-see the welcome card).
  const [started, setStarted] = useState(() =>
    questions.some((question) => isAnswered(intake.answers[question.id])),
  );
  const [phase, setPhase] = useState<'questions' | 'bridge' | 'uploads' | 'summary'>('questions');
  const [uploadIndex, setUploadIndex] = useState(0);

  const save = useMutation({
    mutationFn: async (batch: IntakeAnswers) => {
      const token = (await getToken()) ?? '';
      return savePortalIntakeDraft(engagementId, batch, token);
    },
    onSuccess: (data) => {
      qc.setQueryData(['portal', 'intake', engagementId], data);
      qc.invalidateQueries({ queryKey: ['portal', 'checklist', engagementId] });
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      const token = (await getToken()) ?? '';
      return submitPortalIntake(engagementId, token);
    },
    onSuccess: (data) => {
      qc.setQueryData(['portal', 'intake', engagementId], data);
      qc.invalidateQueries({ queryKey: ['portal', 'checklist', engagementId] });
      setPhase('bridge');
    },
  });

  const files = useQuery({
    queryKey: ['portal', 'files', engagementId],
    enabled: phase === 'uploads',
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return listPortalFiles(engagementId, token);
    },
  });

  const checklist = useQuery({
    queryKey: ['portal', 'checklist', engagementId],
    enabled: phase === 'summary',
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      return getPortalChecklist(engagementId, token);
    },
  });

  const onFilesChanged = () => {
    qc.invalidateQueries({ queryKey: ['portal', 'files', engagementId] });
    qc.invalidateQueries({ queryKey: ['portal', 'checklist', engagementId] });
  };

  const question = questions[index];
  const isLast = index === questions.length - 1;
  const progress = wizardProgress(index, questions.length);

  // Everything edited locally but not yet on the server — includes
  // answers whose earlier save failed, so each advance retries them.
  const pendingDiff = (): IntakeAnswers => {
    const batch: IntakeAnswers = {};
    for (const [id, value] of Object.entries(local)) {
      if (JSON.stringify(value) !== JSON.stringify(intake.answers[id])) batch[id] = value;
    }
    return batch;
  };

  const flushPending = () => {
    const batch = pendingDiff();
    if (Object.keys(batch).length > 0) save.mutate(batch);
  };

  // The final answer must be persisted before submit, or the server can
  // reject the submission for a required answer it hasn't seen yet. The
  // ref guards the window before isPending propagates — a double-click
  // during the awaited save would otherwise submit twice.
  const finishing = useRef(false);
  const finish = async () => {
    if (finishing.current) return;
    finishing.current = true;
    try {
      const batch = pendingDiff();
      if (Object.keys(batch).length > 0) {
        try {
          await save.mutateAsync(batch);
        } catch {
          return; // save error is surfaced below; don't submit unpersisted answers
        }
      }
      await submit.mutateAsync().catch(() => undefined); // error surfaced via submit.isError
    } finally {
      finishing.current = false;
    }
  };

  const advance = () => {
    if (isLast) {
      if (requiredComplete(questions, answers)) void finish();
      return;
    }
    flushPending();
    setIndex(index + 1);
  };

  const skip = advance;

  if (!started) {
    return (
      <WizardShell>
        <div className="grid gap-4 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">
            Welcome — let&apos;s get your engagement set up
          </h1>
          <p className="mx-auto max-w-md text-sm text-brand-slate">
            A few quick questions about your business, one at a time. It takes about three
            minutes, your answers save as you go, and you can skip anything optional and come
            back to it later.
          </p>
          <div>
            <Button type="button" onClick={() => setStarted(true)}>
              Get started
            </Button>
          </div>
        </div>
      </WizardShell>
    );
  }

  if (phase === 'bridge') {
    return (
      <WizardShell>
        <div className="grid gap-4 text-center">
          <span
            aria-hidden
            className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-lg text-white"
          >
            ✓
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">
            Questionnaire complete — thank you
          </h1>
          <p className="mx-auto max-w-md text-sm text-brand-slate">
            Next up: your documents. We&apos;ll walk through them one at a time — your general
            ledger, chart of accounts, invoices, and prior returns — with an example file
            wherever it helps.
          </p>
          <div>
            <Button type="button" onClick={() => setPhase('uploads')}>
              Continue to your documents
            </Button>
          </div>
        </div>
      </WizardShell>
    );
  }

  if (phase === 'uploads') {
    const meta = UPLOAD_CATEGORIES[uploadIndex];
    const uploadProgress = wizardProgress(uploadIndex, UPLOAD_CATEGORIES.length);
    const items = groupByCategory(files.data?.items ?? [])[meta.category];
    const lastUpload = uploadIndex === UPLOAD_CATEGORIES.length - 1;
    return (
      <WizardShell wide>
        <div className="grid gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium uppercase tracking-wide text-brand-slate">
              Your documents
            </span>
            <span className="text-xs tabular-nums text-brand-slate">
              Step {uploadProgress.position} of {uploadProgress.total}
            </span>
          </div>
          <ProgressBar done={uploadProgress.done} total={uploadProgress.total} fullWidth />
        </div>

        {files.isError ? (
          <p className="text-sm text-destructive">
            Couldn&apos;t load your files. Refresh to try again.
          </p>
        ) : (
          <CustomerUploadSection
            engagementId={engagementId}
            meta={meta}
            items={items}
            onChanged={onFilesChanged}
          />
        )}

        <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setUploadIndex(Math.max(uploadIndex - 1, 0))}
            disabled={uploadIndex === 0}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => (lastUpload ? setPhase('summary') : setUploadIndex(uploadIndex + 1))}
          >
            {lastUpload ? 'Review' : items.length > 0 ? 'Next' : 'Skip for now'}
          </Button>
        </div>
      </WizardShell>
    );
  }

  if (phase === 'summary') {
    return (
      <WizardShell wide>
        <div className="grid gap-4">
          <div className="grid gap-1 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">
              You&apos;re set up
            </h1>
            <p className="text-sm text-brand-slate">
              Here&apos;s where your onboarding stands — anything unchecked can be added from
              your dashboard anytime.
            </p>
          </div>
          {checklist.isPending ? (
            <p className="text-center text-sm text-brand-slate">Loading your checklist…</p>
          ) : checklist.data ? (
            <OnboardingChecklist
              data={checklist.data}
              title="Onboarding checklist"
              description="Each item checks itself off as things come in."
            />
          ) : (
            <p className="text-center text-sm text-brand-slate">
              Your dashboard shows what we have and what&apos;s still missing.
            </p>
          )}
          <div className="flex items-center justify-center gap-3">
            <Button type="button" variant="outline" onClick={() => setPhase('uploads')}>
              Back to documents
            </Button>
            <Button type="button" onClick={() => onExit()}>
              Go to your dashboard
            </Button>
          </div>
        </div>
      </WizardShell>
    );
  }

  // A registry content swap could momentarily ship zero questions; the
  // walkthrough then has nothing to ask and completes directly.
  if (question == null) {
    return (
      <WizardShell>
        <div className="grid gap-4 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">
            Nothing to answer right now
          </h1>
          <p className="mx-auto max-w-md text-sm text-brand-slate">
            There are no questionnaire questions at the moment — continue straight to your
            documents.
          </p>
          <div>
            <Button
              type="button"
              onClick={() => void finish()}
              disabled={save.isPending || submit.isPending}
            >
              {submit.isPending ? 'Finishing…' : 'Continue'}
            </Button>
          </div>
        </div>
      </WizardShell>
    );
  }

  const answered = canAdvance(question, answers);
  const onInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && answered && question.answer_type !== 'multi_select') {
      e.preventDefault();
      advance();
    }
  };

  return (
    <WizardShell>
      <div className="grid gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium uppercase tracking-wide text-brand-slate">
            {question.section}
          </span>
          <span className="text-xs tabular-nums text-brand-slate">
            Question {progress.position} of {progress.total}
          </span>
        </div>
        <ProgressBar done={progress.done} total={progress.total} fullWidth />
      </div>

      <div className="grid gap-5" onKeyDown={onInputKeyDown}>
        <div className="grid gap-1">
          <h1
            id={`intake-${question.id}-label`}
            className="text-xl font-semibold tracking-tight text-brand-navy"
          >
            {question.prompt}
          </h1>
          <p className="text-xs text-brand-slate">
            {question.required ? 'Required' : 'Optional — you can fill this in later'}
          </p>
        </div>

        <IntakeInput
          inputId={`intake-${question.id}`}
          question={question}
          value={answers[question.id]}
          autoFocus
          onChange={(value: IntakeAnswerValue) =>
            setLocal((prev) => ({ ...prev, [question.id]: value }))
          }
        />

        <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIndex(Math.max(index - 1, 0))}
            disabled={index === 0}
          >
            Back
          </Button>
          <div className="flex items-center gap-2">
            {!question.required && (
              <Button type="button" variant="ghost" onClick={skip}>
                Skip
              </Button>
            )}
            <Button
              type="button"
              onClick={advance}
              disabled={!answered || submit.isPending || (isLast && save.isPending)}
            >
              {isLast ? (submit.isPending ? 'Finishing…' : 'Finish') : 'Next'}
            </Button>
          </div>
        </div>

        <p className="min-h-4 text-xs" role="status">
          {save.isPending ? (
            <span className="text-brand-slate">Saving…</span>
          ) : save.isError ? (
            <span className="text-destructive">
              Couldn&apos;t save that answer
              {save.error instanceof ApiError ? ` (HTTP ${save.error.status})` : ''} — it will
              retry when you continue.
            </span>
          ) : submit.isError ? (
            <span className="text-destructive">
              {submit.error instanceof ApiError
                ? `Couldn't finish (HTTP ${submit.error.status}).`
                : "Couldn't finish — try again."}
            </span>
          ) : null}
        </p>
      </div>
    </WizardShell>
  );
}

function WizardShell({
  children,
  wide,
}: {
  children: React.ReactNode;
  /** Upload/summary steps embed cards of their own and need the room. */
  wide?: boolean;
}) {
  return (
    <div className={`mx-auto grid w-full gap-4 py-6 ${wide ? 'max-w-2xl' : 'max-w-xl'}`}>
      <Card>
        <CardContent className="grid gap-6 p-6 sm:p-8">{children}</CardContent>
      </Card>
    </div>
  );
}

/** Wizard-visibility rule: the walkthrough is the ONLY onboarding
 *  surface until the questionnaire is submitted — every visit to the
 *  portal's documents view re-enters it. There is deliberately no
 *  dismiss/skip-out: finishing (which submits) is the exit. */
export function shouldShowWizard(intake: IntakeOut | undefined): boolean {
  return intake != null && intake.submitted_at == null;
}
